/**
 * PostHog Error Reporting Utilities
 * 
 * Provides consistent error reporting to PostHog across client and server code.
 * Handles cases where PostHog might not be initialized.
 */

/**
 * Client-side error capture utility
 * Safely captures errors to PostHog with fallback handling
 */
export function captureError(error: Error | unknown, context?: Record<string, any>): void {
  if (typeof window === 'undefined') {
    // Server-side - shouldn't happen, but handle gracefully
    console.error('captureError called on server side:', error)
    return
  }

  // Ensure we have an Error object
  const errorObj = error instanceof Error ? error : new Error(String(error))

  // Try to capture via window.posthog first (most reliable)
  if (window.posthog && typeof window.posthog.captureException === 'function') {
    try {
      window.posthog.captureException(errorObj, {
        ...context,
        source: 'client_catch_block',
        timestamp: new Date().toISOString(),
      })
      return
    } catch (e) {
      console.error('Error capturing exception to PostHog:', e)
    }
  }

  // Fallback: try direct posthog import
  try {
    const posthog = require('posthog-js').default
    if (posthog && typeof posthog.captureException === 'function') {
      posthog.captureException(errorObj, {
        ...context,
        source: 'client_catch_block_fallback',
        timestamp: new Date().toISOString(),
      })
      return
    }
  } catch (e) {
    // PostHog not available - log to console as fallback
    console.error('PostHog not available for error capture:', errorObj, context)
  }
}

/**
 * Server-side error capture utility
 * Captures errors to PostHog server instance
 */
export async function captureServerError(
  error: Error | unknown,
  context?: Record<string, any>
): Promise<void> {
  // Only run in Node.js runtime (not Edge runtime)
  if (process.env.NEXT_RUNTIME !== 'nodejs') {
    console.error('captureServerError called in non-Node.js runtime:', error)
    return
  }

  // Ensure we have an Error object
  const errorObj = error instanceof Error ? error : new Error(String(error))

  try {
    const { getPostHogServer } = require('./posthog-server')
    const posthog = getPostHogServer()

    if (posthog && typeof posthog.captureException === 'function') {
      await posthog.captureException(errorObj, {
        ...context,
        source: 'server_action',
        timestamp: new Date().toISOString(),
      })
    } else {
      console.error('PostHog server instance not available:', errorObj, context)
    }
  } catch (e) {
    // PostHog not available or error in capture - log to console as fallback
    console.error('Error capturing server exception to PostHog:', errorObj, context, e)
  }
}

/**
 * Helper to create error context from common patterns
 */
export function createErrorContext(
  operation: string,
  additionalContext?: Record<string, any>
): Record<string, any> {
  return {
    operation,
    ...additionalContext,
  }
}
